import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  NgZone,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {Alphabet} from '../../../../app-common/const';
import {ToolsService} from '../../../../services/tools.service';
import {ElementToggleEnum} from '../../../../enums/CssClassEnums';
import {QueryParams} from '../../../../types/QueryParams';
import {SearchWidgetContentListItem} from '../../../../types/SearchWidgetContentListItem';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {SearchState} from '../../../../types/SearchState';
import {SearchWidgetUrlBuilderService} from '../../../../services/search-widget/search-widget-url-builder.service';
import {SearchPlaceTypeNameEnum} from '../../../../enums/SearchEnums';
import {SearchPlacesData} from '../../../../types/SearchPlacesData';
import {SearchService} from '../../../../services/search/search.service';

interface ColumnHeaderItem {
  header: string;
  items: SearchWidgetContentListItem[];
}

@Component({
  selector: 'app-places-dropdown-panel-v2',
  templateUrl: './places-dropdown-panel-v2.component.html',
  styleUrls: ['./places-dropdown-panel-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlacesDropdownPanelV2Component {
  topSearchSpacerHeight = input<number>(0);
  cols = input<number>();
  includeFollowLinks = input<boolean>(false);
  isFullscreen = input<boolean>(false);
  placesOptionSelected = output<string>();

  public widgetCssClassName = signal<ElementToggleEnum>(ElementToggleEnum.HIDDEN);
  public itemsFlat = signal<SearchWidgetDataItem[]>(null);
  public placesFollowLinks = signal<SearchWidgetDataItem[]>(null);
  public breadcrumbs = signal<Array<SearchWidgetContentListItem>>([]);
  public columnHeaders = signal<ColumnHeaderItem[]>([]);
  protected readonly elementToggleEnum = ElementToggleEnum;
  private items = signal<SearchWidgetDataItem[]>(null);
  private columnItems = signal<SearchWidgetContentListItem[]>([]);
  private readonly stateUrlPatchField: string = 'placeUrlPath';
  private isDropdownDataPrepared = signal<boolean>(false);

  private searchService = inject(SearchService);
  public state = this.searchService.searchWidgetState;
  private placePanelOpen = this.searchService.placePanelOpen;
  private places = this.searchService.places;
  private searchState = this.searchService.searchState;

  constructor(
    private tools: ToolsService,
    private searchWidgetManagerService: SearchWidgetManagerService,
    private searchWidgetUrlBuilderService: SearchWidgetUrlBuilderService,
    private ngZone: NgZone,
  ) {
    effect(
      () => {
        this.handleOpenPanel(this.placePanelOpen());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        this.handleWidgetData(this.places());
      },
      {allowSignalWrites: true},
    );
  }

  public onCloseClick(): void {
    this.closeThisPanel();
  }

  public goBackTo(level: number): void {
    if (level === 0 || this.breadcrumbs().length <= 1) {
      this.loadChildren(null);
      this.breadcrumbs.set([]);
      return;
    }
    if (level === 1 || this.breadcrumbs().length <= 2) {
      this.loadChildren(this.breadcrumbs()[0]);
      this.breadcrumbs.set([this.breadcrumbs()[0]]);
    }
  }

  public onListItemClick(item: string): void {
    this.placesOptionSelected.emit(item);
    this.closeThisPanel();
  }

  public loadChildren(item: SearchWidgetContentListItem): void {
    if (this.tools.isNil(item)) {
      this.prepareColumnsData(this.items());
      return;
    }

    const foundItem = this.itemsFlat().find((i) => i.id == item.id);

    if (foundItem) {
      this.breadcrumbs.update((prev) => [...prev, item]);
      this.prepareColumnsData(foundItem.children);
      return;
    }

    // Something went very wrong ... just step back and pretend nothing happened.
    this.prepareColumnsData(this.items());
    this.breadcrumbs.set([]);
  }

  private SearchResultsRouterLink(
    item: SearchWidgetDataItem | SearchWidgetContentListItem,
    useLocationSearch: boolean = false,
  ): string[] {
    return this.searchWidgetUrlBuilderService.getSearchResultsRouterLink(
      item,
      this.stateUrlPatchField,
      useLocationSearch,
      false,
      this.searchState(),
      this.state(),
    );
  }

  private SearchResultsQueryParams(
    item: SearchWidgetDataItem | SearchWidgetContentListItem,
    useLocationSearch: boolean = false,
    resetLocation?: boolean,
  ): QueryParams {
    return this.searchWidgetUrlBuilderService.getSearchResultsQueryParams(
      item,
      this.stateUrlPatchField,
      useLocationSearch,
      false,
      resetLocation,
      this.searchState(),
      this.state(),
    );
  }

  private getColumnItems(index: number): SearchWidgetContentListItem[] {
    return this.columnItems()
      .filter((item) => item.columnIndex === index)
      .sort((a: SearchWidgetContentListItem, b: SearchWidgetContentListItem) => this.dropdownItemsSort(a, b));
  }

  private closeThisPanel(): void {
    this.searchService.placePanelOpen.set(false);
  }

  private handleOpenPanel(result: boolean): void {
    const newWidgetClassName = result === true ? ElementToggleEnum.ACTIVE : ElementToggleEnum.HIDDEN;
    if (this.widgetCssClassName() !== newWidgetClassName) {
      this.widgetCssClassName.set(newWidgetClassName);
    }
    if (this.widgetCssClassName() === ElementToggleEnum.ACTIVE) {
      this.searchWidgetManagerService.loadPlaces();
    }
    this.conditionalRunDataPreparation();
  }

  private prepareDropdownDataOnSourceDataLoaded(): void {
    this.itemsFlat.set([]);
    this.flattenPlaces(this.items());
    this.prepareFlatPlacesFollowLinks();
    this.loadChildren(null);
  }

  private prepareFlatPlacesFollowLinks(): void {
    this.placesFollowLinks.set([]);
    this.itemsFlat().forEach((item) => {
      const potentialState: SearchState = this.searchWidgetUrlBuilderService.getSearchResultsRouterLinkPotentialState(
        item,
        this.stateUrlPatchField,
        false,
        false,
        this.searchState(),
        this.state(),
      );
      if (
        item.categoryType !== SearchPlaceTypeNameEnum.COUNTY &&
        item.noFollow === false &&
        (potentialState.caregiverUnitTypeUrlPath !== '' || potentialState.careServiceTypeUrlPath !== '')
      ) {
        this.placesFollowLinks.update((prev) => [...prev, item]);
      }
    });
  }

  private prepareColumnsData(items: SearchWidgetDataItem[]): void {
    this.columnHeaders.set([]);
    this.columnItems.set([]);
    if (!this.tools.isNil(items) && items.length > 0) {
      const itemsPerColumnLimit = Math.ceil(items.length / this.cols());
      const alphabetArray: Array<Array<SearchWidgetDataItem>> = this.getAlphabetArray(items);
      this.loadDataToColumnHeadersAndColumns(alphabetArray, itemsPerColumnLimit);
    }
  }

  private getAlphabetArray(items: SearchWidgetDataItem[]): Array<Array<SearchWidgetDataItem>> {
    const alphabetArray: Array<Array<SearchWidgetDataItem>> = [];
    Alphabet.forEach((alphabetLetter, index) => {
      alphabetArray[index] = items.filter((item) => item.name[0] === alphabetLetter);
    });
    return alphabetArray;
  }

  private loadDataToColumnHeadersAndColumns(
    alphabetArray: Array<Array<SearchWidgetDataItem>>,
    itemsPerColumnLimit: number,
  ): void {
    let colFirstLetter = Alphabet[0];
    let colLastLetter = '';
    let colItemsAdded = 0;
    let columnIndex = 0;

    alphabetArray.forEach((alphabetItems, alphabetLetterIndex) => {
      if (colItemsAdded >= itemsPerColumnLimit) {
        colItemsAdded = 0;
        this.columnHeaders.update((prev) => [
          ...prev,
          {
            header: colFirstLetter + ' - ' + colLastLetter,
            items: [],
          },
        ]);
        columnIndex++;
        colFirstLetter = Alphabet[alphabetLetterIndex];
      }
      colLastLetter = Alphabet[alphabetLetterIndex];
      const portionAdded = this.loadNormalizedItemsToColumnItemsArray(alphabetItems, columnIndex);
      colItemsAdded += portionAdded;
    });

    this.columnHeaders.update((prev) => [
      ...prev,
      {
        header: colFirstLetter + ' - ' + colLastLetter,
        items: [],
      },
    ]);

    this.columnHeaders.update((prev) =>
      prev.map((header, index) => {
        header.items = this.getColumnItems(index);
        return header;
      }),
    );
  }

  private loadNormalizedItemsToColumnItemsArray(alphabetItems: SearchWidgetDataItem[], columnIndex: number): number {
    let portionItemsAdded = 0;
    alphabetItems.forEach((item) => {
      portionItemsAdded++;
      const itemNormalized: SearchWidgetContentListItem = {
        id: item.id,
        name: item.name,
        urlPath: item.urlPath,
        caregiverUnitsCount: item.caregiverUnitsCount,
        columnIndex,
        hasChildren: !this.tools.isNil(item.children) && item.children.length > 0,
        routerLink: this.SearchResultsRouterLink(item, false),
        queryParams: this.SearchResultsQueryParams(item, false),
      };
      this.columnItems.update((prev) => [...prev, itemNormalized]);
    });

    return portionItemsAdded;
  }

  private handleWidgetData(widgetData: SearchPlacesData): void {
    if (!this.tools.isNil(widgetData)) {
      this.items.set(widgetData.places);
      this.isDropdownDataPrepared.set(false);

      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.conditionalRunDataPreparation();
          });
        }, 2000);
      });
    }
  }

  private conditionalRunDataPreparation(): void {
    if (this.isDropdownDataPrepared() === false && this.items() !== null) {
      this.prepareDropdownDataOnSourceDataLoaded();
      this.isDropdownDataPrepared.set(true);
    }
  }

  private dropdownItemsSort(a: SearchWidgetContentListItem, b: SearchWidgetContentListItem): number {
    if (a.name > b.name) {
      return 1;
    }
    if (a.name < b.name) {
      return -1;
    }
    return 0;
  }

  private flattenPlaces(items: SearchWidgetDataItem[]): void {
    items.forEach((item) => {
      item.routerLink = this.SearchResultsRouterLink(item, false);
      item.queryParams = this.SearchResultsQueryParams(item, false);
      this.itemsFlat.update((prev) => [...prev, item]);
      if (item.children != null) {
        this.flattenPlaces(item.children);
      }
    });
  }
}
